/**
 * permissions.js
 * 
 * Centralized role-based permission helpers for the project member system.
 * 
 * Role hierarchy: owner > member > community > null (logged out)
 * 
 * Members are stored in the portal document (portals/{portal_id}).members[]
 * Each member has: { uid, role, status, invite_code, email?, added_at }
 */

/**
 * Compute the current user's project role based on portal data.
 * 
 * @param {Object|null} currentUser - Firebase auth user object
 * @param {Object|null} portal - Portal document data (from state.app.portal)
 * @returns {"owner"|"member"|"community"|null} The user's role
 */
export function computeUserRole(currentUser, portal) {
  if (!currentUser) return null;
  if (currentUser.uid === portal?.creator_id) return "owner";
  const members = portal?.members || [];
  const match = members.find(m => m.uid === currentUser.uid && m.status === "accepted");
  if (match) return "member";
  return "community";
}

// ── Permission check helpers ──

/** Can the user add/delete/reorder shots? */
export const canEditShots = (role) => role === "owner" || role === "member";

/** Can the user add/delete/reorder scenes (parts)? */
export const canEditParts = (role) => role === "owner" || role === "member";

/** Can the user edit shot titles? */
export const canEditShotTitle = (role) => role === "owner" || role === "member";

/** Can the user edit shot descriptions? */
export const canEditShotDescription = (role) => role === "owner" || role === "member";

/** Can the user access the Story Audio sidebar link? */
export const canAccessStoryAudio = (role) => role === "owner" || role === "member";

/** Can the user access the View as Strip sidebar link? */
export const canViewAsStrip = (role) => role === "owner" || role === "member";

/** Can the user start workflows? */
export const canStartWorkflow = (role) => role === "owner" || role === "member";

/** Can the user manage project members (add/remove)? Owner only. */
export const canManageMembers = (role) => role === "owner";

/** Can the user manage project settings? Owner only. */
export const canManageSettings = (role) => role === "owner";

/** Is the user at least a member (owner or member)? General-purpose check. */
export const isAtLeastMember = (role) => role === "owner" || role === "member";

/**
 * Can the user trim this take (and persist its skyline cache)? Mirrors the Take View trim gate.
 *
 * @param {{ isStoryOwner: boolean, role: string|null, communityCanTrim: boolean, currentUid: string|null, take: Object|null, veneer: Object|null, autostory: boolean }} params
 */
export function canTrimTake({ isStoryOwner, role, communityCanTrim, currentUid, take, veneer, autostory }) {
  if (autostory) return false;
  return !!(
    isStoryOwner ||
    isAtLeastMember(role) ||
    communityCanTrim ||
    (currentUid && take?.uploader_id === currentUid) ||
    (currentUid && veneer?.creator_id === currentUid)
  );
}

// ── Portal password gate ──

/**
 * Does the user have access to a (possibly) password-protected portal?
 *
 * Access is granted when the portal has no password, when the current user is
 * the portal creator, or when a matching password was stored for this portal.
 *
 * Stored entries are written by PassDialog as `{ id, pass }`. Legacy entries
 * using `{ id, password }` are also accepted.
 *
 * @param {Object|null} portal - Portal document data ({ id, requirePass, creator_id })
 * @param {Object|null} currentUser - Firebase auth user object
 * @param {Array<Object>} storedPasswords - Parsed sessionStorage 'passwords' array
 * @returns {boolean}
 */
export function hasPortalPassAccess(portal, currentUser, storedPasswords = []) {
  const required = portal?.requirePass;
  if (required === "" || required === null || required === undefined) return true;
  if (currentUser?.uid && currentUser.uid === portal?.creator_id) return true;
  if (!Array.isArray(storedPasswords)) return false;
  return storedPasswords.some(p =>
    p && p.id === portal?.id && (p.pass === required || p.password === required)
  );
}

